import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  QrCodeScanner as QrScanIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Payments as PaidIcon,
} from '@mui/icons-material';
import LoadingAnimation from '../components/LoadingAnimation';
import QrScanner from '../components/QrScanner';
import customerService from '../services/customer.service';
import merchantService from '../services/merchant.service';
import { useAuth } from '../contexts/AuthContext';

// Use dynamic import for QRCode to avoid TypeScript issues
const QRCode = require('qrcode');

export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMerchant = user?.role === 'merchant';
  const isCustomer = user?.role === 'customer';
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [paymentUpdating, setPaymentUpdating] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [scannerError, setScannerError] = useState('');

  useEffect(() => {
    if (orderId) {
      loadOrderDetails();
    }
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      // Fetch order from API based on role
      let orderData;
      if (isMerchant) {
        orderData = await merchantService.getOrder(orderId!);
      } else {
        orderData = await customerService.getOrderDetails(orderId!);
      }
      console.log('Order data:', orderData);
      setOrder(orderData);

      // Generate QR code
      const qrData = JSON.stringify({
        orderId: orderData.orderNumber,
        pickupCode: orderData.pickupCode,
        customerName: orderData.customer?.name,
        total: orderData.pricing?.total,
      });

      const qrDataUrl = await QRCode.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#0B6E4F',
          light: '#FFFFFF',
        },
      });

      setQrCode(qrDataUrl);
    } catch (error: any) {
      console.error('Error loading order:', error);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `bitehub-order-${order.orderNumber}.png`;
    link.click();
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!order || !isMerchant) return;

    try {
      setStatusUpdating(true);
      await merchantService.updateOrderStatus(order._id, newStatus);
      await loadOrderDetails(); // Reload order to show updated status
    } catch (error: any) {
      console.error('Error updating order status:', error);
      alert(error.response?.data?.error || error.response?.data?.message || 'Failed to update order status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!order || !isMerchant) return;

    try {
      setPaymentUpdating(true);
      await merchantService.markOrderPaid(order._id);
      await loadOrderDetails(); // Reload order to show updated payment status
    } catch (error: any) {
      console.error('Error marking order paid:', error);
      alert(error.response?.data?.error || error.response?.data?.message || 'Failed to update payment');
    } finally {
      setPaymentUpdating(false);
    }
  };

  const handleOpenScanner = () => {
    setShowQrScanner(true);
    setScannerError('');
  };

  const handleCloseScanner = () => {
    setShowQrScanner(false);
    setScannerError('');
  };

  const handleScanSuccess = (scannedData: any) => {
    // Two QR formats can show up here: the mobile app encodes the pickup
    // code as plain text (e.g. "PU384021"), while this dashboard's own
    // customer-order view encodes JSON ({ pickupCode, orderId, ... }).
    // Try a direct string match first (the real-world case, since orders
    // are placed from the app), then fall back to JSON for the other path,
    // instead of assuming JSON and failing on every plain-text scan.
    const raw = typeof scannedData === 'string' ? scannedData.trim() : scannedData;

    let scannedCode: string | undefined;
    if (typeof raw === 'string') {
      if (raw === order.pickupCode) {
        scannedCode = raw;
      } else {
        try {
          const parsed = JSON.parse(raw);
          scannedCode = parsed?.pickupCode;
        } catch {
          scannedCode = undefined;
        }
      }
    } else if (raw && typeof raw === 'object') {
      scannedCode = raw.pickupCode;
    }

    if (!scannedCode) {
      setScannerError('Failed to read QR code. Please try again.');
      return;
    }

    if (scannedCode === order.pickupCode) {
      alert('✓ Pickup code verified! You can hand over the order.');
      handleCloseScanner();

      // Optionally update status to collected
      if (order.status === 'ready') {
        handleStatusChange('collected');
      }
    } else {
      setScannerError('Invalid QR code. This code does not match this order.');
    }
  };

  const handlePrint = () => {
    if (!order) return;

    // Generate HTML invoice
    const invoiceHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice - ${order.orderNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Arial', sans-serif; padding: 40px; background: #f5f5f5; }
    .invoice { max-width: 800px; margin: 0 auto; background: white; padding: 40px; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
    .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px; border-bottom: 3px solid #0B6E4F; padding-bottom: 20px; }
    .logo { font-size: 32px; font-weight: bold; color: #0B6E4F; }
    .logo .bite { color: #FF7A00; }
    .logo .hub { color: #0B6E4F; }
    .company-info { text-align: right; color: #666; font-size: 14px; }
    .invoice-title { font-size: 28px; color: #333; margin-bottom: 30px; }
    .invoice-details { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .detail-group { flex: 1; }
    .detail-label { font-weight: bold; color: #666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px; }
    .detail-value { color: #333; font-size: 14px; margin-bottom: 15px; }
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
    .items-table th { background: #0B6E4F; color: white; padding: 12px; text-align: left; font-size: 12px; text-transform: uppercase; }
    .items-table td { padding: 12px; border-bottom: 1px solid #eee; }
    .items-table tr:last-child td { border-bottom: none; }
    .totals { margin-left: auto; width: 300px; }
    .total-row { display: flex; justify-content: space-between; padding: 8px 0; }
    .total-row.grand { font-size: 20px; font-weight: bold; color: #0B6E4F; border-top: 2px solid #0B6E4F; margin-top: 10px; padding-top: 15px; }
    .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #eee; text-align: center; color: #666; font-size: 12px; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: bold; text-transform: uppercase; }
    .status-paid { background: #d4edda; color: #155724; }
    .status-pending { background: #fff3cd; color: #856404; }
    @media print {
      body { background: white; padding: 0; }
      .invoice { box-shadow: none; padding: 20px; }
      @page {
        margin: 0;
        size: A4;
      }
      @page :first {
        margin-top: 0;
      }
    }
  </style>
</head>
<body>
  <div class="invoice">
    <div class="header">
      <div class="logo">
        <span class="bite">bite</span><span class="hub">hub</span>
      </div>
      <div class="company-info">
        <div style="font-weight: bold; font-size: 16px; color: #0B6E4F; margin-bottom: 5px;">BiteHub</div>
        <div>Keep Good Food in the Loop</div>
        <div>Fight Food Waste Together</div>
      </div>
    </div>

    <div class="invoice-title">INVOICE</div>

    <div class="invoice-details">
      <div class="detail-group">
        <div class="detail-label">Invoice Number</div>
        <div class="detail-value">${order.orderNumber}</div>

        <div class="detail-label">Date</div>
        <div class="detail-value">${new Date(order.createdAt).toLocaleString()}</div>

        <div class="detail-label">Payment Status</div>
        <div class="detail-value">
          <span class="status-badge ${order.payment?.status === 'completed' ? 'status-paid' : 'status-pending'}">
            ${order.payment?.status === 'completed' ? 'Paid' : 'Pending'}
          </span>
        </div>
      </div>

      <div class="detail-group">
        <div class="detail-label">Customer</div>
        <div class="detail-value">${order.customer?.name || 'N/A'}</div>

        <div class="detail-label">Order Status</div>
        <div class="detail-value" style="text-transform: capitalize;">${order.status}</div>
      </div>
    </div>

    <table class="items-table">
      <thead>
        <tr>
          <th>Description</th>
          <th>Merchant</th>
          <th style="text-align: center;">Quantity</th>
          <th style="text-align: right;">Price</th>
          <th style="text-align: right;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${(order.items || []).map((item: any) => `
          <tr>
            <td><strong>${item.surpriseBox?.name || 'Surprise Box'}</strong></td>
            <td>${item.surpriseBox?.business?.name || item.business?.name || 'Merchant'}</td>
            <td style="text-align: center;">${item.quantity}</td>
            <td style="text-align: right;">OMR ${item.price.toFixed(2)}</td>
            <td style="text-align: right;">OMR ${(item.quantity * item.price).toFixed(2)}</td>
          </tr>
        `).join('')}
        ${(order.menuItems || []).map((mi: any) => `
          <tr>
            <td><strong>${mi.menuItem?.name || 'Menu Item'}</strong></td>
            <td>${mi.menuItem?.business?.name || mi.business?.name || 'Merchant'}</td>
            <td style="text-align: center;">${mi.quantity}</td>
            <td style="text-align: right;">OMR ${mi.price.toFixed(2)}</td>
            <td style="text-align: right;">OMR ${(mi.quantity * mi.price).toFixed(2)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="totals">
      <div class="total-row">
        <span>Subtotal:</span>
        <span>OMR ${order.pricing?.subtotal?.toFixed(2) || '0.00'}</span>
      </div>
      ${order.pricing?.discount ? `
      <div class="total-row" style="color: #28a745;">
        <span>Discount:</span>
        <span>- OMR ${order.pricing.discount.toFixed(2)}</span>
      </div>
      ` : ''}
      <div class="total-row">
        <span>Delivery Fee:</span>
        <span style="color: #28a745;">FREE</span>
      </div>
      <div class="total-row grand">
        <span>TOTAL:</span>
        <span>OMR ${order.pricing?.total?.toFixed(2) || '0.00'}</span>
      </div>
    </div>

    <div class="footer">
      <div style="margin-bottom: 10px; font-weight: bold; color: #0B6E4F;">Thank you for helping reduce food waste! 🌍</div>
      <div>This is a computer-generated invoice and does not require a signature.</div>
      <div style="margin-top: 10px;">Questions? Contact us at support@bitehub.com</div>
    </div>
  </div>
</body>
</html>
    `;

    // Open in new window and print
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(invoiceHTML);
      printWindow.document.close();

      // Wait for content to load then print
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();

        // Add event listener to close window after print
        printWindow.onafterprint = function() {
          printWindow.close();
        };
      }, 250);
    }
  };

  if (loading) {
    return <LoadingAnimation message="Loading order details..." subtitle="Please wait" />;
  }

  if (!order) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Order not found
        </Typography>
        <Button variant="contained" onClick={() => navigate('/orders')}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  const statusColor: any = {
    pending: 'warning',
    confirmed: 'info',
    ready: 'success',
    collected: 'success',
    cancelled: 'error',
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/orders')}>
          Back to Orders
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Order Details */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Order {order.orderNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Placed on {new Date(order.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <Chip
                  label={order.status.toUpperCase()}
                  color={statusColor[order.status]}
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              {isMerchant && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Manage Order
                  </Typography>

                  {/* Accept / reject a newly placed (pending) order */}
                  {order.status === 'pending' && (
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<AcceptIcon />}
                        disabled={statusUpdating}
                        onClick={() => handleStatusChange('confirmed')}
                      >
                        Accept Order
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<RejectIcon />}
                        disabled={statusUpdating}
                        onClick={() => handleStatusChange('cancelled')}
                      >
                        Reject Order
                      </Button>
                    </Box>
                  )}

                  {/* Cash orders start unpaid; let the merchant confirm payment on pickup */}
                  {order.payment?.method === 'cash' &&
                    order.payment?.status !== 'completed' &&
                    order.status !== 'cancelled' && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PaidIcon />}
                        disabled={paymentUpdating}
                        onClick={handleMarkPaid}
                        sx={{ mb: 2 }}
                      >
                        Mark Cash as Paid
                      </Button>
                    )}

                  <FormControl fullWidth size="small">
                    <InputLabel>Change Status</InputLabel>
                    <Select
                      value={order.status}
                      label="Change Status"
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={statusUpdating || order.status === 'collected' || order.status === 'cancelled'}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="confirmed">Confirmed</MenuItem>
                      <MenuItem value="ready">Ready for Pickup</MenuItem>
                      <MenuItem value="collected">Collected</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Customers will be notified when you update the order status
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Order Items
              </Typography>

              {(order.items || []).map((item: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 3,
                    pb: 3,
                    borderBottom: index < (order.items?.length || 0) - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  <Box
                    component="img"
                    src={item.surpriseBox?.images?.[0] || 'https://via.placeholder.com/100'}
                    alt={item.surpriseBox?.name || 'Product'}
                    sx={{
                      width: 100,
                      height: 100,
                      objectFit: 'cover',
                      borderRadius: 2,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                      {item.surpriseBox?.name || 'Surprise Box'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {item.surpriseBox?.business?.name || item.business?.name || 'Merchant'}
                    </Typography>
                    {(item.surpriseBox?.business?.location?.address || item.business?.location?.address) && (
                      <Typography variant="body2" color="text.secondary">
                        📍 {item.surpriseBox?.business?.location?.address || item.business?.location?.address}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Qty: {item.quantity} × <RialSymbol /> {item.price.toFixed(2)} = <RialSymbol />{' '}
                      {(item.quantity * item.price).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {(order.menuItems || []).map((mi: any, index: number) => (
                <Box
                  key={`menu-${index}`}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 3,
                    pb: 3,
                    borderBottom: index < (order.menuItems?.length || 0) - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  <Box
                    component="img"
                    src={mi.menuItem?.images?.[0] || 'https://via.placeholder.com/100'}
                    alt={mi.menuItem?.name || 'Menu Item'}
                    sx={{
                      width: 100,
                      height: 100,
                      objectFit: 'cover',
                      borderRadius: 2,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                      {mi.menuItem?.name || 'Menu Item'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {mi.menuItem?.business?.name || mi.business?.name || 'Merchant'}
                    </Typography>
                    {(mi.menuItem?.business?.location?.address || mi.business?.location?.address) && (
                      <Typography variant="body2" color="text.secondary">
                        📍 {mi.menuItem?.business?.location?.address || mi.business?.location?.address}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Qty: {mi.quantity} × <RialSymbol /> {mi.price.toFixed(2)} = <RialSymbol />{' '}
                      {(mi.quantity * mi.price).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              ))}

              <Divider sx={{ my: 3 }} />

              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>Subtotal</TableCell>
                    <TableCell align="right">
                      <RialSymbol /> {order.pricing.subtotal.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  {order.pricing.discount > 0 && (
                    <TableRow>
                      <TableCell>Discount</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        - <RialSymbol /> {order.pricing.discount.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>Delivery Fee</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      FREE
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700 }}>Total</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                        <RialSymbol /> {order.pricing.total.toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Customer Information
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>{order.customer?.name || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Phone</TableCell>
                    <TableCell>{order.customer?.phone || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Payment Method</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {order.payment?.method || order.paymentMethod || 'N/A'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Payment Status</TableCell>
                    <TableCell>
                      <Chip
                        label={order.payment?.status === 'completed' ? 'Paid' : 'Pending'}
                        color={order.payment?.status === 'completed' ? 'success' : 'warning'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Box>

        {/* QR Code - Only for customers */}
        <Box>
          {isCustomer && (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Pickup QR Code
                </Typography>

                <Box
                  sx={{
                    bgcolor: 'white',
                    p: 3,
                    borderRadius: 2,
                    border: '2px solid #eee',
                    mb: 3,
                  }}
                >
                  <Box
                    component="img"
                    src={qrCode}
                    alt="Pickup QR Code"
                    sx={{ width: '100%', maxWidth: 300 }}
                  />
                </Box>

                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    p: 2,
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Pickup Code
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 2 }}>
                    {order.pickupCode}
                  </Typography>
                </Box>

                <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
                  Show this QR code or pickup code to the delivery partner when collecting your order.
                </Alert>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadQR}
                  sx={{ mb: 1 }}
                >
                  Download QR Code
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                >
                  Print Order
                </Button>
              </CardContent>
            </Card>
          )}

          {isMerchant && (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Pickup Information
                </Typography>

                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    p: 3,
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Pickup Code
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: 3 }}>
                    {order.pickupCode}
                  </Typography>
                </Box>

                <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
                  Verify this pickup code when the customer collects their order.
                </Alert>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<QrScanIcon />}
                  onClick={handleOpenScanner}
                  sx={{ mb: 1 }}
                >
                  Scan Customer QR Code
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                >
                  Print Order
                </Button>
              </CardContent>
            </Card>
          )}

          <Card sx={{ background: 'linear-gradient(135deg, #0B6E4F 0%, #B1D65D 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Order Status: {order.status.toUpperCase()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {order.status === 'pending' && 'Your order is being prepared'}
                {order.status === 'confirmed' && 'Your order has been confirmed'}
                {order.status === 'ready' && 'Your order is ready for pickup!'}
                {order.status === 'collected' && 'Order completed. Thank you!'}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* QR Scanner Dialog for Merchants */}
      {isMerchant && (
        <Dialog
          open={showQrScanner}
          onClose={handleCloseScanner}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Scan Customer QR Code</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              <QrScanner
                onScan={handleScanSuccess}
                onError={(error) => setScannerError(error)}
              />
              {scannerError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {scannerError}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseScanner}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
